<template>
  <modal-vue
    @close="close"
    :modalActive="modalActive"
    :has-header-border="true"
    :has-footer-border="true"
  >
    <template #header>
      <p class="font-bold text-start text-lg">Editar Usuário</p>
    </template>

    <v-form v-if="!loadingInitialData" ref="formRef" @submit.prevent="updateUser">
      <v-row dense class="mt-4">
        <v-col cols="12" md="6">
          <GenericInput
            v-model="form.name"
            label="Nome*"
            :rules="[rules.required, rules.name]"
            variant="outlined"
          />
        </v-col>

        <v-col cols="12" md="6">
          <GenericInput v-model="form.email" label="Email" variant="outlined" :disabled="true" />
        </v-col>

        <v-col cols="12" md="6">
          <GenericInput
            v-model="form.phone1"
            label="Telefone*"
            :rules="[rules.required, rules.phone]"
            v-maska="'(##) #####-####'"
            variant="outlined"
          />
        </v-col>

        <v-col cols="12" md="6">
          <GenericInput
            v-model="form.registration"
            label="Matrícula"
            :rules="[]"
            variant="outlined"
            placeholder="Não definida"
          />
        </v-col>

        <v-col cols="12">
          <v-autocomplete
            v-model="form.companies"
            :items="listCompanies"
            item-title="title"
            item-value="value"
            label="Empresas*"
            multiple
            chips
            closable-chips
            variant="outlined"
            color="primary20"
            :rules="[rules.requiredList]"
          />
        </v-col>
      </v-row>
    </v-form>
    <div v-else class="text-center py-10">
      <v-progress-circular indeterminate color="primary" />
      <p class="mt-4 text-gray-600">Carregando dados do usuário...</p>
    </div>

    <template #footer>
      <v-row dense justify="end">
        <v-col cols="12" md="auto">
          <OutlineButton @click="close" text="Cancelar" width="140px" />
        </v-col>
        <v-col cols="12" md="auto">
          <FilledButton
            :loading="loadingSubmit"
            @click="updateUser"
            text="Salvar Alterações"
            width="180px"
            type="submit"
          />
        </v-col>
      </v-row>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { VForm } from 'vuetify/components'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import Network from '@/Network'
import { useToast } from 'vue-toastification'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import { UpdateUserPayload } from '@/Network/Types/Requests/CompanyRequests'
import { CompanyUserResponse } from '@/Network/Types/Responses/CompanyResponses'
import { storeToRefs } from 'pinia'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'

const props = defineProps<{
  modalActive: boolean
  userData: CompanyUserResponse | null
}>()

const emits = defineEmits(['close', 'user-updated'])
const formRef = ref<InstanceType<typeof VForm> | null>(null)
const loadingSubmit = ref(false)
const toast = useToast()
const loadingInitialData = ref(false)

const { listCompanies } = storeToRefs(useCompanyStore())

const getInitialFormState = () => ({
  name: '',
  email: '',
  phone1: '',
  registration: null as string | null,
  companies: [] as string[]
})
const form = reactive(getInitialFormState())

const rules = {
  required: (v: any) => !!v || 'Campo obrigatório',
  requiredList: (v: any[]) => (v && v.length > 0) || 'Selecione pelo menos uma empresa',
  name: (v: string) => (v && v.length >= 3) || 'Nome deve ter no mínimo 3 caracteres',
  phone: (v: string) => (v && v.length === 15) || 'Telefone inválido'
}

watch(
  () => props.userData,
  (newUserData) => {
    if (newUserData && props.modalActive) {
      loadingInitialData.value = true
      form.name = newUserData.user.name
      form.email = newUserData.user.email
      form.phone1 = newUserData.user.mainPhone?.phoneNumber || ''
      form.registration = newUserData.user.registration ?? null
      form.companies = newUserData.companies.map((c: any) => String(c.id))

      loadingInitialData.value = false
    } else if (!props.modalActive) {
      resetForm()
    }
  },
  { immediate: true, deep: true }
)

const resetForm = () => {
  Object.assign(form, getInitialFormState())
  formRef.value?.resetValidation()
}

const close = () => {
  resetForm()
  emits('close')
}

const updateUser = async () => {
  if (!formRef.value || !props.userData) return

  const { valid } = await formRef.value.validate()
  if (!valid) {
    toast.warning('Por favor, preencha corretamente os campos obrigatórios.')
    return
  }

  try {
    loadingSubmit.value = true

    const payload: UpdateUserPayload = {
      name: form.name,
      phone: form.phone1.replace(/\D/g, ''),
      registration: form.registration ?? undefined,
      companies: form.companies.map((id) => Number(id))
    }

    await Network.company.updateUser(props.userData.user.id, payload)
    toast.success('Usuário atualizado com sucesso!')
    emits('user-updated')
    close()
  } catch (e: any) {
    const message = e.response?.data?.message || 'Erro ao atualizar usuário. Tente novamente!'
    toast.error(message)
  } finally {
    loadingSubmit.value = false
  }
}
</script>
